/**
 * Plain data of a geo camera plan (as synced by Zero or returned by the
 * public DTO) and the derived model every renderer uses: measured tracks,
 * gradient runs, marks per item and route coverage.
 */
import { measureTrack, } from "./measure.js";
import { gradientRuns, orientProfile, scaleProfile, profileStats, } from "./profile.js";
import { DEFAULT_MARK_MERGE_DISTANCE_M, DEFAULT_MARK_SNAP_DISTANCE_M, itemTrackMarks, lineCoverage, viewAreaCoverage, } from "./marks.js";
import { cameraDisplayName, cameraProfileDisplay, getSymbolType, itemDisplayLabel, } from "./symbols.js";
export function buildTrackModel(track) {
    const measured = measureTrack(track.geometry.coordinates, {
        reversed: track.reversed,
    });
    // The profile ends at the stored length; stretch it onto the measured
    // line so marks, gradient runs and the chart line up
    const profile = orientProfile(scaleProfile(track.profile ?? [], measured.length), measured.length, track.reversed);
    return {
        track,
        measured,
        profile,
        stats: profileStats(profile),
        runs: gradientRuns(profile, measured.length),
        forMarks: { id: track.id, measured, profile },
        profilePending: profile.length === 0,
    };
}
function byOrder(a, b) {
    return (a.sortOrder ?? 0) - (b.sortOrder ?? 0);
}
/**
 * Items in list order: camera groups ordered by their first position, a
 * camera's positions by letter, other items in place.
 */
export function orderItems(items) {
    const sorted = [...items].sort(byOrder);
    const out = [];
    const seenCameras = new Set();
    for (const item of sorted) {
        if (!item.cameraID) {
            out.push(item);
            continue;
        }
        if (seenCameras.has(item.cameraID))
            continue;
        seenCameras.add(item.cameraID);
        out.push(...sorted
            .filter((i) => i.cameraID === item.cameraID)
            .sort((a, b) => (a.positionLetter ?? "").localeCompare(b.positionLetter ?? "")));
    }
    return out;
}
export function buildGeoPlanModel(data, { trackModels, visibleTrackIds, } = {}) {
    const snapDistanceM = data.plan.markSnapDistanceM ?? DEFAULT_MARK_SNAP_DISTANCE_M;
    const mergeDistanceM = data.plan.markMergeDistanceM ?? DEFAULT_MARK_MERGE_DISTANCE_M;
    const tracks = [...data.tracks]
        .sort(byOrder)
        .map((track) => trackModels?.get(track.id) ?? buildTrackModel(track));
    const markTracks = tracks
        .filter((t) => !visibleTrackIds || visibleTrackIds.has(t.track.id))
        .map((t) => t.forMarks);
    const camerasById = new Map(data.cameras.map((c) => [c.id, c]));
    const areasByItem = new Map();
    for (const area of data.viewAreas) {
        const list = areasByItem.get(area.itemID) ?? [];
        list.push(area);
        areasByItem.set(area.itemID, list);
    }
    const positionCounts = new Map();
    for (const item of data.items) {
        if (item.cameraID) {
            positionCounts.set(item.cameraID, (positionCounts.get(item.cameraID) ?? 0) + 1);
        }
    }
    const items = orderItems(data.items).map((item) => {
        const camera = item.cameraID
            ? (camerasById.get(item.cameraID) ?? null)
            : null;
        const symbol = getSymbolType(camera ? camera.icon : item.symbolType);
        const positionCount = item.cameraID
            ? (positionCounts.get(item.cameraID) ?? 1)
            : 1;
        const viewAreas = (areasByItem.get(item.id) ?? []).sort(byOrder);
        // Drawn lines and text are annotations, not places on the route
        const marks = symbol.drawing && symbol.key !== "marker"
            ? []
            : itemTrackMarks(item, markTracks, { snapDistanceM, mergeDistanceM });
        const profileDisplay = camera ? cameraProfileDisplay(camera) : null;
        // Lanes show where a camera sees the route; moving cameras use km ranges
        const coversRoute = !!symbol.coversRoute || (profileDisplay === "lane" && !symbol.ridesRoute);
        const coverage = [];
        if (coversRoute) {
            for (const track of markTracks) {
                if (symbol.kind === "line" && item.line) {
                    const range = lineCoverage(item.line, track, snapDistanceM);
                    if (range)
                        coverage.push(range);
                }
                else if (item.point &&
                    viewAreas.length &&
                    // A lane shows coverage even while the map hides the view area
                    (item.showViewAreas || profileDisplay === "lane")) {
                    coverage.push(...viewAreaCoverage(item.point, viewAreas.map((a) => a.vertices), track));
                }
            }
        }
        if (item.followTrackID &&
            item.followFromM != null &&
            item.followToM != null &&
            coversRoute &&
            markTracks.some((t) => t.id === item.followTrackID)) {
            coverage.push({
                trackId: item.followTrackID,
                fromM: Math.min(item.followFromM, item.followToM),
                toM: Math.max(item.followFromM, item.followToM),
            });
        }
        return {
            item,
            symbol,
            camera,
            label: itemDisplayLabel(item, camera, positionCount),
            baseLabel: camera
                ? cameraDisplayName(camera.number)
                : itemDisplayLabel(item, null),
            positionCount,
            profileDisplay,
            viewAreas,
            marks,
            coverage,
        };
    });
    return {
        data,
        tracks,
        items,
        itemsById: new Map(items.map((i) => [i.item.id, i])),
        camerasById,
        snapDistanceM,
        mergeDistanceM,
    };
}
/** "Men km 3.2 · Women km 1.1 / 7.9" */
export function formatItemKms(marks, tracks, { withNames = tracks.length > 1 } = {}) {
    return tracks
        .map((t) => {
        const kms = marks
            .filter((m) => m.trackId === t.track.id)
            .sort((a, b) => a.m - b.m)
            .map((m) => (m.m / 1000).toFixed(1));
        if (!kms.length)
            return null;
        return withNames
            ? `${t.track.name} km ${kms.join(" / ")}`
            : `km ${kms.join(" / ")}`;
    })
        .filter(Boolean)
        .join(" · ");
}
/** Distinct track colours, in order of creation. */
export const TRACK_COLORS = [
    "#E5174F",
    "#7C3AED",
    "#0EA5E9",
    "#F97316",
    "#10B981",
    "#EAB308",
    "#EC4899",
    "#64748B",
];
export function nextTrackColor(used) {
    return (TRACK_COLORS.find((c) => !used.includes(c)) ??
        TRACK_COLORS[used.length % TRACK_COLORS.length]);
}
